/**
 * Typed Hub API client.
 *
 * v0.1 uses a hand-rolled fetch wrapper. Once the Hub publishes its
 * OpenAPI 3.1 spec (M20b), swap in `openapi-fetch` via:
 *
 *     import createClient from "openapi-fetch";
 *     import type { paths } from "./hub-openapi";
 *     export const hub = createClient<paths>({ baseUrl: ... });
 */

const HUB_API_URL =
  (import.meta.env.VITE_HUB_API_URL as string | undefined) ??
  "https://hub.sumo.com";

type RequestBody = Record<string, unknown> | undefined;

export interface HubResult<T = unknown> {
  response: Response;
  data: T | null;
}

async function call<T = unknown>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  opts: { body?: RequestBody; params?: Record<string, string> } = {},
): Promise<HubResult<T>> {
  const url = new URL(`${HUB_API_URL}${path}`);
  if (opts.params) {
    for (const [k, v] of Object.entries(opts.params)) {
      url.searchParams.set(k, v);
    }
  }

  const headers: Record<string, string> = {
    "content-type": "application/json",
    accept: "application/json",
  };

  // CSRF: double-submit token. The login flow sets a non-HttpOnly
  // `csrf_token` cookie (readable from JS) alongside the session cookie.
  if (method !== "GET") {
    const csrf = readCookie("csrf_token");
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  const body = opts.body === undefined ? undefined : JSON.stringify(opts.body);

  let response = await fetch(url.toString(), {
    method,
    headers,
    body,
    credentials: "include",
  });

  // Auto-refresh once on 401 — covers the 1h session expiry.
  if (response.status === 401 && path !== "/oauth/session/refresh") {
    const refresh = await fetch(`${HUB_API_URL}/oauth/session/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (refresh.ok) {
      response = await fetch(url.toString(), {
        method,
        headers,
        body,
        credentials: "include",
      });
    }
  }

  let data: T | null = null;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json") && response.status !== 204) {
    data = (await response.clone().json().catch(() => null)) as T | null;
  }

  return { response, data };
}

export const hub = {
  GET: <T = unknown>(
    path: string,
    opts: { params?: Record<string, string> } = {},
  ) => call<T>("GET", path, opts),
  POST: <T = unknown>(path: string, opts: { body?: RequestBody } = {}) =>
    call<T>("POST", path, opts),
  PATCH: <T = unknown>(path: string, opts: { body?: RequestBody } = {}) =>
    call<T>("PATCH", path, opts),
  DELETE: <T = unknown>(path: string) => call<T>("DELETE", path),
};

function readCookie(name: string): string | null {
  const match = new RegExp(`(?:^|; )${name}=([^;]+)`).exec(document.cookie);
  return match ? decodeURIComponent(match[1]) : null;
}
